<script setup>
import { computed, reactive, ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useAuthService } from '~/composables/useAuthService';
import { useValidation } from '~/composables/useValidation';
import { useMessaging } from '~/composables/useMessaging';
import AvatarImage from '@/components/images/AvatarImage.vue';

const props = defineProps({
  user: {
    type: Object,
    required: true
  },
  size: {
    type: String,
    default: 'small',
    validator: (value) => ['small', 'medium', 'large'].includes(value)
  },
  expandable: {
    type: Boolean,
    default: true
  },
  inline: {
    type: Boolean,
    default: true
  },
  minimized: {
    type: Boolean,
    default: false
  },
  highlighted: {
    type: Boolean,
    default: false
  },
  highlightColor: {
    type: String,
    default: 'info'
  }
});

const router = useRouter();
const route = useRoute();
const { user: currentUser, isAuthenticated, isSuperUser, impersonateUser, impersonateLoading } = useAuthService();
const { isGuid } = useValidation();
const { createConversation } = useMessaging();
const messageLoading = ref(false);

const state = reactive({
  dialog: false
});

const handleClick = () => {
  if (props.expandable) {
    state.dialog = true;
  }
};

const style = computed(() => {
  return {
    cursor: props.expandable ? 'pointer' : 'default'
  };
});

const nameStyle = computed(() => {
  return {
    fontSize: props.size === 'small' ? '14px' : (props.size === 'medium' ? '18px' : '24px')
  };
});

const avatarSize = computed(() => {
  return props.size === 'small' ? 20 : (props.size === 'medium' ? 30 : 45);
});

const avatarClasses = computed(() => {
  return {
    'mr-1': !props.minimized && props.size === 'small',
    'mr-2': !props.minimized && props.size !== 'small'
  };
});

const fullName = computed(() => {
  return `${props.user.firstName} ${props.user.lastName}`;
});

const initials = computed(() => {
  const first = props.user.firstName?.charAt(0) || '';
  const last = props.user.lastName?.charAt(0) || '';
  return (first + last).toUpperCase() || '?';
});

// Handle message navigation
const handleMessage = async () => {
  if (!props.user?.id) return;

  try {
    messageLoading.value = true;
    const conversation = await createConversation([props.user.id], 1, null);
    router.push({
      query: { ...route.query, conversationId: conversation.id, messaging: 'open' }
    });
    state.dialog = false;
  } catch (error) {
    console.error('Failed to create conversation:', error);
  } finally {
    messageLoading.value = false;
  }
};

// Handle profile navigation
const handleProfile = () => {
  if (props.user.playerId) {
    router.push({
      name: 'PlayerDetail',
      params: { playerId: props.user.playerId }
    });
    state.dialog = false;
  }
};

// Handle stats navigation
const handleStats = () => {
  if (props.user.playerId) {
    router.push({
      name: 'PlayerStats',
      params: { playerId: props.user.playerId }
    });
    state.dialog = false;
  }
};

// Handle impersonate
const handleImpersonate = async () => {
  if (props.user.id) {
    await impersonateUser(props.user.id);
    state.dialog = false;
  }
};
</script>

<template>
  <div v-if="user" :style="style" @click="handleClick" :class="inline ? 'flex-wrap d-flex' : ''">
    <div class="d-flex font-weight-bold align-center">
      <v-badge icon="mdi-arrow-bottom-left" :model-value="highlighted" :color="highlightColor" :floating="true">
        <v-tooltip :disabled="!minimized" :text="fullName" location="bottom">
          <template #activator="{ props: tooltipProps }">
            <avatar-image
              v-bind="tooltipProps"
              :size="avatarSize"
              :class="avatarClasses"
              :filename="user.imageFileName"
              :fallback="initials"
            />
            <div v-if="!minimized" class="align-center d-flex" :style="nameStyle">
              <span>{{ fullName }}</span>
            </div>
          </template>
        </v-tooltip>
      </v-badge>
    </div>
  </div>

  <!-- Expandable dialog -->
  <v-dialog v-model="state.dialog" max-width="600">
    <v-card>
      <v-card-title class="pa-4 d-flex justify-space-between align-center border-b">
        <user-card :expandable="false" size="large" :user="user" />
        <v-btn icon="mdi-close" variant="text" size="small" @click="state.dialog = false" />
      </v-card-title>

      <v-card-text class="pa-4 border-b">
        <div class="d-flex flex-wrap gap-2">
          <v-chip v-if="user.email" class="meta-chip">
            <v-icon size="small" class="mr-2">mdi-email</v-icon>
            {{ user.email }}
          </v-chip>
          <v-chip v-if="user.telephone" class="meta-chip">
            <v-icon size="small" class="mr-2">mdi-phone</v-icon>
            {{ user.telephone }}
          </v-chip>
        </div>
      </v-card-text>

      <v-card-actions class="pa-4">
        <!-- Message button -->
        <v-btn
          v-if="isAuthenticated && isGuid(user.id)"
          size="small"
          variant="flat"
          color="primary"
          :loading="messageLoading"
          @click="handleMessage"
        >
          <v-icon class="mr-2">mdi-chat</v-icon>
          Message
        </v-btn>

        <!-- Stats button (if user has a player) -->
        <v-btn
          v-if="user.playerId"
          size="small"
          variant="flat"
          color="primary"
          @click="handleStats"
        >
          <v-icon class="mr-2">mdi-chart-line</v-icon>
          Stats
        </v-btn>

        <!-- Profile button (if user has a player) -->
        <v-btn
          v-if="user.playerId"
          size="small"
          variant="flat"
          color="primary"
          @click="handleProfile"
        >
          <v-icon class="mr-2">mdi-account</v-icon>
          Profile
        </v-btn>

        <!-- Impersonate button (super users only) -->
        <v-btn
          v-if="isSuperUser && isGuid(user.id) && user.id !== currentUser?.id"
          size="small"
          variant="flat"
          color="primary"
          :loading="impersonateLoading"
          :disabled="!isGuid(user.id)"
          @click="handleImpersonate"
        >
          <v-icon class="mr-2">mdi-lock</v-icon>
          Login
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.border-b {
  border-bottom: 1px solid rgb(var(--v-theme-borderLight));
}

/* contact chips — neutral hairline pills (recolour only) */
.meta-chip {
  background: rgb(var(--v-theme-light)) !important;
  border: 1px solid rgb(var(--v-theme-borderLight)) !important;
  color: rgb(var(--v-theme-charcoal)) !important;
  font-size: 11px;
  font-weight: 600;
}

.meta-chip :deep(.v-chip__underlay) {
  opacity: 0;
}
</style>
